import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type SignatureDetailsResponse = {
  downloadUrl: string | null;
  hasSignature: boolean;
  signoffText: string | null;
};

type AdminUploadUrlResponse = {
  uploadUrl: string;
  uploadToken: string;
};

const adviserSignatureKey = (userId: string) => ['users', userId, 'signature'];

export const useAdviserSignature = (userId: string | null) => {
  const { data, isLoading, isError } = useQuery<SignatureDetailsResponse>({
    queryKey: adviserSignatureKey(userId ?? ''),
    enabled: !!userId,
  });

  return {
    downloadUrl: data?.downloadUrl ?? null,
    hasSignature: data?.hasSignature ?? false,
    signoffText: data?.signoffText ?? null,
    isLoading,
    isError,
  };
};

export const useUploadAdviserSignature = (userId: string | null) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<void, Error, File>({
    mutationFn: async (file: File) => {
      if (!userId) throw new Error('No adviser selected');

      const { data: uploadUrlResponse } = await api.post<AdminUploadUrlResponse>(
        `users/${userId}/signature/upload-url`,
        { contentType: file.type },
      );

      const s3Response = await fetch(uploadUrlResponse.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!s3Response.ok) {
        throw new Error(`Failed to upload to S3: ${s3Response.statusText}`);
      }

      await api.post(`users/${userId}/signature/confirm`, {
        uploadToken: uploadUrlResponse.uploadToken,
      });
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: adviserSignatureKey(userId) });
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return {
    uploadSignature: mutateAsync,
    isLoading,
  };
};

export const useUpdateAdviserSignoff = (userId: string | null) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, string>({
    mutationFn: (signoffText: string) => {
      if (!userId) throw new Error('No adviser selected');
      return api.put(`users/${userId}/signature/signoff`, { signoffText: signoffText || null });
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: adviserSignatureKey(userId) });
      }
    },
  });

  return {
    updateSignoffText: mutateAsync,
    isLoading,
  };
};

export const useDeleteAdviserSignature = (userId: string | null) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<unknown, Error, void>({
    mutationFn: () => {
      if (!userId) throw new Error('No adviser selected');
      return api.delete(`users/${userId}/signature`);
    },
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: adviserSignatureKey(userId) });
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return {
    deleteSignature: mutateAsync,
    isLoading,
  };
};
